/** Read a value from window.ENV_CONFIG with a fallback default. */
const env = (key: string, fallback: any) =>
  (window as any).ENV_CONFIG?.[key] ?? fallback;

/**
 * Reporting system configuration flags.
 *
 * Toggle features here instead of hunting through 1000+ lines of component code.
 * These are compile-time defaults; runtime overrides come from window.ENV_CONFIG.
 */
export const REPORT_CONFIG = {
  /** Auto-select the first template when the editor is empty.
   *  Derived from ENV_CONFIG.DISABLE_TEMPLATE_AUTO_SELECTION (inverted). */
  AUTO_LOAD_TEMPLATE_WHEN_EMPTY: !env('DISABLE_TEMPLATE_AUTO_SELECTION', false),

  /** Pre-select the template body part from the order's po_body_part.
   *  Defaults to OFF — the reporting radiologist picks it themselves.
   *  Override at runtime with ENV_CONFIG.AUTO_SELECT_NODE_FROM_BODYPART. */
  AUTO_SELECT_NODE_FROM_BODYPART: env('AUTO_SELECT_NODE_FROM_BODYPART', false),

  /** Show the clinically correlated / diagnosed radio buttons */
  SHOW_CORRELATED_DIAGNOSED: true,

  /** Require "Clinically Diagnosed" + "Clinically Correlated" before sign-off.
   *  Defaults to OFF. Derived from ENV_CONFIG.FORCE_DIAGNOSIS_SIGNOFF. */
  FORCE_DIAGNOSIS_SIGNOFF: env('FORCE_DIAGNOSIS_SIGNOFF', false),

  /** Show the "Notify Physician" / co-sign / proxy options panel */
  SHOW_ADVANCED_OPTIONS: true,

  /** Show the "Notify Physician" button */
  SHOW_NOTIFY_PHYSICIAN: true,

  /** Show proxy-draft and proxy-signoff options (subset of advanced) */
  SHOW_PROXY_OPTIONS: true,

  /** Place the report header and footer outside the editor iframe instead
   *  of inside the editable content.  Header appears between toolbar and
   *  editing area; footer appears below it.
   *  When false, both are injected inside the iframe as before.
   *  Derived from ENV_CONFIG.HEADER_FOOTER_EXTERNAL. */
  HEADER_FOOTER_EXTERNAL: env('HEADER_FOOTER_EXTERNAL', true),

  /** Make the report header non-editable inside the editor.
   *  Derived from ENV_CONFIG.HEADER_NON_EDITABLE. */
  HEADER_NON_EDITABLE: env('HEADER_NON_EDITABLE', true),

  /** Show the Print Report button in the editor footer */
  SHOW_PRINT_BUTTON: true,

  /** Keep-alive ping interval in milliseconds */
  KEEP_ALIVE_INTERVAL_MS: 15_000,

  /** Default footer disclaimer when backend config is unavailable */
  DEFAULT_FOOTER_TEXT:
    'Note: This is a professional opinion only, Each investigation has its limitations. Final diagnosis needs correlation with clinical context and other investigations. Kindly discuss, if necessary.',
} as const;

/** Frontend status code  ->  backend API value */
export const STATUS_MAP: Record<string, string> = {
  DRAFTED: 'draft',
  REVIEWED: 'reviewed',
  SIGNEDOFF: 'signoff',
};

/** Backend API value  ->  frontend status code */
export const REVERSE_STATUS_MAP: Record<string, string> = {
  draft: 'DRAFTED',
  reviewed: 'REVIEWED',
  signoff: 'SIGNEDOFF',
};

/** Allowed file types for document viewer */
export const ALLOWED_FILE_TYPES: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
};
